import * as auth from "./auth";
import { brandMark, brandName, icon } from "./icons";
import { api, clear, closeAllLayers, emptyState, h, menu, openViewer } from "./lib";
import { creditLabel, go, LOW_CREDITS, refreshMe, state, type Config } from "./store";
import { renderAdmin } from "./views/admin";
import { renderLogin } from "./views/login";
import { renderProject } from "./views/project";
import { renderProjects } from "./views/projects";

const root = document.getElementById("app")!;

function header(): HTMLElement {
  const me = state.me!;
  const dev = auth.isDevMode();
  const onAdmin = (location.hash || "").startsWith("#/admin");
  const avatar = h("button", { class: "avatar", type: "button", "aria-label": `계정 메뉴 (${me.email})` }, (me.email[0] || "?").toUpperCase());
  return h(
    "header",
    { class: "top" },
    h(
      "div",
      { class: "top-left" },
      h("a", { class: "brand", href: "#/", "aria-label": "STAGE X 아트웍 · 내 프로젝트" }, brandMark(32), brandName(14)),
      dev
        ? state.config?.fakeAi
          ? h("span", { class: "chip warn", title: "이 PC 시험 서버예요. 그림은 가짜 AI가 만들어요." }, "시험 서버")
          : h("span", { class: "chip warn", title: "이 PC 시험 서버예요. 그림은 진짜 AI가 만들어서 구글 잔액이 줄어요." }, "시험 서버 · 진짜 AI")
        : null,
    ),
    h(
      "nav",
      { class: "top-right", "aria-label": "계정" },
      h("span", { id: "credit-badge", class: `credit-pill${me.credits < LOW_CREDITS ? " low" : ""}`, title: creditLabel(me.credits), "aria-label": creditLabel(me.credits), role: "status" }, icon("coins", 16), h("span", { class: "v num" }, String(me.credits))),
      me.admin ? h("a", { class: "top-link", href: "#/admin", "aria-current": onAdmin ? "page" : null }, icon("shield", 16), h("span", { class: "label" }, "운영")) : null,
      dev
        ? null
        : menu(
            [
              {
                label: "로그아웃",
                icon: "logout",
                onclick: async () => {
                  await auth.logout();
                  state.me = null;
                  go("#/");
                },
              },
            ],
            { trigger: avatar, head: me.email, cls: "account-menu" },
          ),
    ),
  );
}

let rendering = 0;
/** 서버가 로그인을 거절했다(401). 다시 로그인할 때까지 /api/me 를 되풀이하지 않고 로그인 화면에 머문다 */
let rejected = false;

async function render() {
  const my = ++rendering;
  const hash = location.hash || "#/";
  let fresh = false;
  if (!state.me && !rejected && (await auth.loggedIn())) {
    try {
      await refreshMe();
      fresh = true;
    } catch {
      state.me = null;
    }
  }
  if (my !== rendering) return;
  clear(root);
  delete document.body.dataset.phase;
  if (!state.me) {
    root.append(
      await renderLogin(async () => {
        rejected = false;
        await refreshMe();
        go("#/");
      }),
    );
    return;
  }
  const main = h("main", { class: "page", id: "main" });
  root.append(header(), main);
  const parts = hash.replace(/^#\/?/, "").split("/").filter(Boolean);
  try {
    if (parts[0] === "p" && parts[1]) {
      // 프로젝트를 불러오는 동안 머리·단계 표시줄 모양을 먼저 보여 준다
      main.append(h("div", { class: "boot-skeleton", "aria-hidden": "true" }, h("div", { class: "skeleton", style: { height: "14px", width: "120px" } }), h("div", { class: "skeleton", style: { height: "28px", width: "40%" } }), h("div", { class: "skeleton", style: { height: "56px", width: "100%" } })));
      // 늦게 끝난 옛 화면이 새 화면을 건드리지 않게 지금 차례인지 넘겨 준다
      await renderProject(main, parts[1], parts[2] || "", () => my === rendering);
    } else if (parts[0] === "admin") {
      await renderAdmin(main);
    } else {
      await renderProjects(main);
    }
  } catch (err) {
    if (my !== rendering) return;
    clear(main);
    main.append(emptyState({ icon: "alert", title: "화면을 열지 못했어요", body: err instanceof Error ? err.message : String(err), action: h("a", { class: "btn", href: "#/" }, "처음으로") }));
  }
  // 머리띠 크레딧을 실제 잔액에 맞춘다 (기다리지 않는다). 방금 받아 왔으면 건너뛴다
  if (!fresh && my === rendering) void refreshMe().catch(() => {});
}

async function boot() {
  try {
    state.config = await api<Config>("/api/config");
  } catch {
    clear(root);
    root.append(h("main", { class: "page" }, emptyState({ icon: "alert", title: "서버에 연결하지 못했어요", body: "잠시 뒤 다시 시도해 주세요.", action: h("button", { class: "btn", type: "button", onclick: () => location.reload() }, icon("refresh", 16), "다시 시도") })));
    return;
  }
  auth.setDevMode(state.config.devAuth);
  // 작은 그림(장소 그림, 저장한 시점, 다른 방향, 소스)을 누르면 크게 본다. 같은 묶음([data-gallery])끼리 넘겨 본다
  const zoom = (img: HTMLImageElement) => {
    const group = img.closest("[data-gallery]");
    const imgs = group ? [...group.querySelectorAll<HTMLImageElement>("img.zoomable")] : [img];
    openViewer(
      imgs.map((x) => ({ src: x.src, title: x.alt || "그림", checker: Boolean(x.closest(".checker")), download: x.dataset.download })),
      Math.max(0, imgs.indexOf(img)),
    );
  };
  document.addEventListener("click", (e) => {
    const img = (e.target as HTMLElement)?.closest?.("img.zoomable") as HTMLImageElement | null;
    if (!img) return;
    e.preventDefault();
    zoom(img);
  });
  // 크게 볼 수 있는 그림은 Tab 으로 들어가 Enter·Space 로 연다
  new MutationObserver(() => {
    for (const img of document.querySelectorAll<HTMLImageElement>("img.zoomable:not([tabindex])")) {
      img.tabIndex = 0;
      img.setAttribute("role", "button");
      img.title = img.title || "크게 보기";
    }
  }).observe(document.body, { childList: true, subtree: true });
  document.addEventListener("keydown", (e) => {
    const img = e.target as HTMLElement;
    if ((e.key !== "Enter" && e.key !== " ") || !(img instanceof HTMLImageElement) || !img.classList.contains("zoomable")) return;
    e.preventDefault();
    zoom(img);
  });
  window.addEventListener("hashchange", () => {
    closeAllLayers();
    void render();
  });
  window.addEventListener("rerender", () => void render());
  window.addEventListener("auth-expired", () => {
    // 401 이 이어져도 한 번만 로그인 화면으로 보낸다
    if (rejected) return;
    rejected = true;
    state.me = null;
    void render();
  });
  await render();
}

void boot();
